import { getSimulatorType } from "@/lib/simulatorType"
import {
  SimulatorType,
  WebSocketConnectionMode,
} from "@/types/api/SimulatorType"
import type { FormData } from "@/types/FormData"
import type { RequiredKey } from "@/types/SimulatorConfig"
import {
  isSipAddress,
  isValidSipAddress,
} from "@/utils/persona/phoneNumberUtils"
import { validateAgentForm } from "@/utils/validation/agentValidation"

// Pipecat's required keys render in the generic form with their raw key names
// (pipecat_api_key / agent_name) to match app.coval, so SIMULATOR_FIELDS
// intentionally has no labels for them. Map them to friendly tooltip names for
// the create-button hover without changing the form field labels.
const PIPECAT_TOOLTIP_LABELS: Record<string, string[]> = {
  pipecat_api_key: ["API Key"],
  agent_name: ["Agent Name"],
}

const MISSING_REQUIRED_FIELDS_PREFIX = "Missing required fields: "

export function getAgentCreateValidationIssues(
  validationError: string | null | undefined,
): string[] {
  if (!validationError) {
    return []
  }
  if (!validationError.startsWith(MISSING_REQUIRED_FIELDS_PREFIX)) {
    return [validationError]
  }
  return validationError
    .slice(MISSING_REQUIRED_FIELDS_PREFIX.length)
    .split(", ")
    .map((field) => `${field} is required.`)
}

interface ValidateAgentCreateOptions {
  /** Validate the name separately when reporting connection completion. */
  validateName?: boolean
  /**
   * The model type persisted on the agent being edited. An edit that still
   * carries this exact type passes the unsupported-type check, because the
   * backend accepts updates that keep a read-only agent type and rejects
   * legacy types only on create. Omit it for new agents, where any
   * unrecognized type stays an error.
   */
  persistedModelType?: string | null
  /** Allows an unchanged unsupported value when validating an existing agent. */
  persistedMediaEncryption?: unknown
}

export function validateAgentCreate(
  formData: FormData,
  requiredFields: RequiredKey[],
  options: ValidateAgentCreateOptions = {},
): string | null {
  const missingFields: string[] = []

  if (options.validateName !== false && !formData.display_name.trim()) {
    missingFields.push("Name")
  }
  if (!formData.model_type) {
    missingFields.push("Agent Type")
  }

  const simulatorType = formData.model_type
    ? getSimulatorType(formData.model_type)
    : null
  const isUnchangedPersistedType =
    options.persistedModelType != null &&
    formData.model_type === options.persistedModelType
  if (formData.model_type && !simulatorType && !isUnchangedPersistedType) {
    return "Unsupported agent type"
  }

  if (simulatorType === SimulatorType.VOICE && !formData.phone_number) {
    missingFields.push("Phone number or SIP address")
  }
  // SMS reaches the agent over text at a phone number, stored on
  // formData.phone_number (not in SIMULATOR_FIELDS). Kept in its own block,
  // separate from the voice case, so each agent type's rules stay independent.
  if (simulatorType === SimulatorType.SMS && !formData.phone_number) {
    missingFields.push("Phone Number")
  }

  for (const field of requiredFields) {
    // Treat whitespace-only values as missing so generic required-field validation agrees with optional JSON-blob validators and the form's required indicator.
    const fieldValue = formData.metadata[field.key]
    const isFieldMissing =
      !fieldValue ||
      (typeof fieldValue === "string" && fieldValue.trim() === "")
    if (field.optional !== true && isFieldMissing) {
      const pipecatLabels =
        simulatorType === SimulatorType.PIPECAT
          ? PIPECAT_TOOLTIP_LABELS[field.key]
          : undefined
      if (pipecatLabels) {
        missingFields.push(...pipecatLabels)
      } else {
        missingFields.push(field.label || field.key)
      }
    }
  }

  // WebSocket required fields depend on the connection mode. These are not in
  // SIMULATOR_FIELDS (kept empty so the config toggle can mount), so surface
  // them here by name for the create-button hover.
  if (simulatorType === SimulatorType.WEBSOCKET) {
    if (
      formData.metadata.connection_mode === WebSocketConnectionMode.HTTP_FIRST
    ) {
      if (!formData.metadata.http_url) {
        missingFields.push("HTTP Endpoint URL")
      }
      if (!formData.metadata.websocket_url_response_path) {
        missingFields.push("WebSocket URL Path in Response")
      }
    } else if (
      formData.metadata.connection_mode ===
      WebSocketConnectionMode.TWIML_WEBHOOK
    ) {
      if (!formData.metadata.voice_url) {
        missingFields.push("Voice Webhook URL")
      }
    } else if (!formData.metadata.endpoint) {
      missingFields.push("WebSocket Endpoint")
    }
  }

  // Chat agents surface their required endpoint here too, in dedicated blocks
  // per protocol. These fields live outside SIMULATOR_FIELDS (the chat config
  // components render them directly, and CHAT_WEBSOCKET keeps SIMULATOR_FIELDS
  // empty so its connection-mode toggle can mount). Kept separate from the voice
  // WebSocket block above so each agent type's required-field rules can evolve
  // independently — chat WebSocket, for example, does not require Initialization
  // JSON. This also keeps chat from colliding with edits to the voice block.
  if (
    simulatorType === SimulatorType.TEXT &&
    !formData.metadata.chat_endpoint
  ) {
    missingFields.push("Chat Endpoint")
  }
  if (simulatorType === SimulatorType.CHAT_WEBSOCKET) {
    if (
      formData.metadata.connection_mode === WebSocketConnectionMode.HTTP_FIRST
    ) {
      if (!formData.metadata.http_url) {
        missingFields.push("HTTP Endpoint URL")
      }
      if (!formData.metadata.websocket_url_response_path) {
        missingFields.push("WebSocket URL Path in Response")
      }
    } else if (!formData.metadata.endpoint) {
      missingFields.push("WebSocket Endpoint")
    }
  }

  // LiveKit's required fields are not in SIMULATOR_FIELDS (LIVEKIT is kept
  // empty), so surface them here by name so the Create-agent tooltip lists the
  // real missing fields for the LiveKit transport.
  if (simulatorType === SimulatorType.LIVEKIT) {
    if (!formData.metadata.generate_token_endpoint) {
      missingFields.push("Token Generation Endpoint")
    }
    if (!formData.metadata.livekit_url) {
      missingFields.push("LiveKit URL")
    }
    // The Dispatch Mode selector's own selection (Explicit vs Automatic)
    // lives in metadata.livekit_dispatch_mode — a transient, save-stripped
    // field (see getLiveKitMetadataForSave / useDispatchMode) that exists
    // only so this check can see it, since dispatch mode is otherwise meant
    // to be fully derivable from livekit_agent_name alone. Without this,
    // selecting Explicit dispatch and leaving Agent Name blank rendered the
    // field as visually required but saved successfully anyway — silently
    // producing an automatic-dispatch agent while the UI kept showing
    // Explicit selected (COVAL-4534).
    if (
      formData.metadata.livekit_dispatch_mode === "explicit" &&
      !formData.metadata.livekit_agent_name
    ) {
      missingFields.push("Agent Name (required for explicit dispatch)")
    }
  }

  // Outbound voice's trigger endpoint is not in SIMULATOR_FIELDS either. It is
  // written to metadata.trigger_call_endpoint and mirrored onto the top-level
  // endpoint, so treat it as present when either holds a value.
  if (
    simulatorType === SimulatorType.OUTBOUND_VOICE &&
    !formData.metadata.trigger_call_endpoint &&
    !formData.endpoint
  ) {
    missingFields.push("Trigger Call Endpoint")
  }

  if (missingFields.length > 0) {
    return `${MISSING_REQUIRED_FIELDS_PREFIX}${missingFields.join(", ")}`
  }

  // All required fields are present — now validate their formats.
  if (simulatorType === SimulatorType.EMAIL) {
    const validation = validateAgentForm(formData, {
      validateName: options.validateName,
    })
    if (!validation.success) {
      return validation.error || "Validation failed"
    }
  }

  if (
    simulatorType === SimulatorType.VOICE &&
    isSipAddress(formData.phone_number) &&
    !isValidSipAddress(formData.phone_number)
  ) {
    return "Please enter a valid SIP address (e.g. sip:user@example.com)"
  }

  // Voice create must enforce the same metadata rules as edit-save. In
  // particular, enabled media encryption is only valid with a SIP address.
  if (simulatorType === SimulatorType.VOICE) {
    const validation = validateAgentForm(formData, {
      persistedMediaEncryption: options.persistedMediaEncryption,
      validateName: options.validateName,
    })
    if (!validation.success) {
      return validation.error || "Validation failed"
    }
  }

  // Mirrors the backend's save-time check (agent_provider_metadata_validation.py
  // :: validate_livekit_metadata): livekit_agent_name and a custom room_config
  // both set at once is a conflict — the agent name should be the sole source
  // of the room_config the backend generates. Uses the same tolerant
  // JSON-parsing approach as useLiveKitAdvancedConfig's validateJSON (invalid
  // JSON here is saved as typed, not blocked, so only a *successfully parsed*
  // room_config key is treated as a conflict).
  if (
    simulatorType === SimulatorType.LIVEKIT &&
    formData.metadata.livekit_agent_name
  ) {
    const customPayload = formData.metadata.token_request_payload
    if (customPayload) {
      try {
        const parsedPayload =
          typeof customPayload === "string"
            ? (JSON.parse(customPayload) as unknown)
            : customPayload
        if (
          parsedPayload &&
          typeof parsedPayload === "object" &&
          "room_config" in parsedPayload
        ) {
          return "Agent Name and a custom room_config in Custom Payload Fields conflict — remove one."
        }
      } catch {
        // Invalid JSON is saved as typed elsewhere in this form; nothing to
        // conflict-check here.
      }
    }
  }

  if (simulatorType === SimulatorType.WEBSOCKET) {
    const validation = validateAgentForm(formData, {
      validateName: options.validateName,
    })
    if (!validation.success) {
      return validation.error || "Validation failed"
    }
  }

  // Chat WebSocket reuses the same form-level format validation (endpoint
  // scheme, JSON fields). validateAgentForm branches on simulator type
  // internally, so this stays decoupled from the voice WebSocket case above.
  if (simulatorType === SimulatorType.CHAT_WEBSOCKET) {
    const validation = validateAgentForm(formData, {
      validateName: options.validateName,
    })
    if (!validation.success) {
      return validation.error || "Validation failed"
    }
  }

  if (
    simulatorType === SimulatorType.OPENAI_REALTIME ||
    simulatorType === SimulatorType.DEEPGRAM_REALTIME
  ) {
    const validation = validateAgentForm(formData, {
      validateName: options.validateName,
    })
    if (!validation.success) {
      return validation.error || "Validation failed"
    }
  }

  return null
}
